import type {
  MediaAuthorsApiArticle,
  MediaAuthorsApiArticlesPage,
  MediaAuthorsApiAuthorPageResponse,
  MediaAuthorsApiCatalogAuthor,
  MediaAuthorsApiIndexableResponse,
  MediaAuthorsApiPopulatedAuthor,
  MediaAuthorsApiSeoHubResponse,
} from '../api/media-authors-api.types';
import type {
  MediaAuthorArticleListItem,
  MediaAuthorArticlesPage,
  MediaAuthorListItem,
  MediaAuthorPageData,
  MediaAuthorProfile,
  MediaAuthorsCatalog,
  MediaAuthorsCatalogSeo,
  MediaAuthorsIndexablePage,
  PopulatedMediaAuthor,
} from '../types';
import { buildTelegramUrl } from './build-telegram-url.util';

const DEFAULT_ARTICLES_LIMIT = 12;

function mapPopulatedAuthor(author: MediaAuthorsApiPopulatedAuthor): PopulatedMediaAuthor {
  return {
    id: author.id,
    name: author.name,
    avatar: author.avatar ?? null,
    slug: author.slug ?? null,
    url: author.url ?? null,
    isActive: author.isActive,
    isPublished: author.isActive,
  };
}

function mapArticle(article: MediaAuthorsApiArticle): MediaAuthorArticleListItem {
  const heroImageUrl =
    article.heroImage?.url ?? article.heroImage?.thumbnailURL ?? null;
  return {
    id: String(article.id),
    slug: article.slug ?? '',
    title: article.title,
    description: article.description ?? '',
    publishedAt: article.publishedAt ?? '',
    type: article.type ?? undefined,
    heroImageUrl,
    populatedAuthors: (article.populatedAuthors ?? []).map(mapPopulatedAuthor),
  };
}

function mapArticlesPage(
  articles: MediaAuthorsApiArticlesPage,
  limit: number,
): MediaAuthorArticlesPage {
  return {
    docs: articles.docs.map(mapArticle),
    page: articles.page,
    limit,
    totalDocs: articles.totalDocs,
    hasNextPage: articles.hasNextPage,
  };
}

function mapCatalogAuthor(author: MediaAuthorsApiCatalogAuthor): MediaAuthorListItem {
  return {
    slug: author.username,
    name: author.name,
    firstName: author.firstName ?? null,
    lastName: author.lastName ?? null,
    jobTitle: author.jobTitle ?? null,
    bio: author.bio?.trim() || null,
    avatarUrl: author.avatar ?? null,
    articlesCount: author.articlesCount,
    url: author.url,
  };
}

function mapCatalogSeo(hub: MediaAuthorsApiSeoHubResponse): MediaAuthorsCatalogSeo {
  return {
    h1: hub.h1,
    topText: hub.seoTextTop ?? null,
    bottomText: hub.seoTextBottom ?? null,
    seoTitle: hub.title,
    seoDescription: hub.description,
  };
}

function mapProfile(response: MediaAuthorsApiAuthorPageResponse): MediaAuthorProfile {
  const telegramUrl = buildTelegramUrl(response.telegramId);
  const vkUrl = response.vk?.trim() || null;
  const email = response.email?.trim() || null;
  const hasAnyContact = Boolean(telegramUrl || vkUrl || email);
  return {
    slug: response.username,
    name: response.name,
    firstName: response.firstName ?? null,
    lastName: response.lastName ?? null,
    jobTitle: response.jobTitle ?? null,
    avatarUrl: response.avatar ?? null,
    coverUrl: response.background ?? null,
    about: response.bio ?? null,
    education: response.education?.length ? response.education : null,
    contacts: hasAnyContact ? { telegramUrl, vkUrl, email } : null,
    mediaMentions: (response.mediaMentions ?? []).map((mention) => ({
      title: mention.title,
      year: mention.year ?? null,
      url: mention.url ?? null,
      logoUrl: mention.logo?.url ?? null,
    })),
    expertise: response.expertise ?? [],
    articlesCount: response.articlesCount,
    seoTitle: response.seoTitle?.trim() || response.name,
    seoDescription: response.seoDescription?.trim() || '',
    jsonLd: response.jsonLd,
  };
}

export function mapMediaAuthorsCatalog(
  authorsResponse: { docs: MediaAuthorsApiCatalogAuthor[] },
  hubResponse: MediaAuthorsApiSeoHubResponse,
): MediaAuthorsCatalog {
  return {
    seo: mapCatalogSeo(hubResponse),
    authors: authorsResponse.docs.map(mapCatalogAuthor),
  };
}

export function mapMediaAuthorPage(
  response: MediaAuthorsApiAuthorPageResponse,
  limit: number = DEFAULT_ARTICLES_LIMIT,
): MediaAuthorPageData {
  return {
    profile: mapProfile(response),
    articlesPage: mapArticlesPage(response.articles, limit),
  };
}

export function mapMediaAuthorArticlesPage(
  response: MediaAuthorsApiAuthorPageResponse,
  limit: number = DEFAULT_ARTICLES_LIMIT,
): MediaAuthorArticlesPage {
  return mapArticlesPage(response.articles, limit);
}

export function mapMediaAuthorsIndexable(
  response: MediaAuthorsApiIndexableResponse,
  siteUrl: string,
): MediaAuthorsIndexablePage {
  const baseUrl = siteUrl.replace(/\/$/, '');
  const resolveUrl = (loc: string): string =>
    loc.startsWith('http') ? loc : `${baseUrl}${loc.startsWith('/') ? loc : `/${loc}`}`;
  return {
    catalog: response.catalog
      ? {
          url: resolveUrl(response.catalog.loc),
          lastModified: new Date(response.catalog.lastmod),
        }
      : null,
    docs: response.docs.map((entry) => ({
      url: resolveUrl(entry.loc),
      lastModified: new Date(entry.lastmod),
    })),
    hasNextPage: response.hasNextPage,
    page: response.page,
    limit: response.limit,
  };
}

export const MEDIA_AUTHORS_DEFAULT_ARTICLES_LIMIT = DEFAULT_ARTICLES_LIMIT;
